import { useState, useCallback, useRef } from 'react';
import { agrupamentoService, type Agrupamento, type AgrupamentoSugestaoDto } from '../api/agrupamento';

export const useAgrupamentos = () => {
  const [agrupamentos, setAgrupamentos] = useState<Agrupamento[]>([]);
  const [sugestoesMap, setSugestoesMap] = useState<Record<number, AgrupamentoSugestaoDto>>({});
  const [carregando, setCarregando] = useState(false);
  const [termoBusca, setTermoBusca] = useState('');
  const loadingRef = useRef(false);

  const carregarAgrupamentos = useCallback(async () => {
    // Evita chamadas paralelas duplicadas (StrictMode, re-renders rápidos)
    if (loadingRef.current) return;
    loadingRef.current = true;
    setCarregando(true);
    try {
      const [dados, sugestoes] = await Promise.all([
        agrupamentoService.listarAgrupamentos('', true),
        agrupamentoService.listarSugestoes().catch(() => ({})),
      ]);
      setAgrupamentos(dados);
      setSugestoesMap(sugestoes);
    } catch {
      setAgrupamentos([]);
    } finally {
      setCarregando(false);
      loadingRef.current = false;
    }
  }, []);

  const criarAgrupamento = useCallback(async (descricao: string) => {
    if (!descricao.trim()) return false;
    try {
      await agrupamentoService.criarAgrupamento({ descricao: descricao.trim() });
      await carregarAgrupamentos();
      return true;
    } catch {
      return false;
    }
  }, [carregarAgrupamentos]);

  const excluirAgrupamento = useCallback(async (agrupamento: Agrupamento) => {
    try {
      await agrupamentoService.excluirAgrupamento(agrupamento.grupoId);
      await carregarAgrupamentos();
      return true;
    } catch {
      return false;
    }
  }, [carregarAgrupamentos]);

  const renomearAgrupamento = useCallback(async (grupoId: number, descricao: string) => {
    const trimmed = descricao.trim();
    if (!trimmed) return false;
    try {
      await agrupamentoService.renomearAgrupamento(grupoId, trimmed);
      // Atualização otimista local + refresh em paralelo pra cobrir cache invalidation no backend
      setAgrupamentos(prev => prev.map(a => a.grupoId === grupoId ? { ...a, descricao: trimmed } : a));
      await carregarAgrupamentos();
      return true;
    } catch {
      return false;
    }
  }, [carregarAgrupamentos]);

  // Atualização otimista: decrementa sugestão localmente ao adicionar produtos
  const decrementarSugestao = useCallback((grupoId: number, qtde: number) => {
    setSugestoesMap(prev => {
      const atual = prev[grupoId];
      if (!atual) return prev;
      const novaQtde = atual.qtdeSugeridos - qtde;
      if (novaQtde <= 0) {
        const { [grupoId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [grupoId]: { ...atual, qtdeSugeridos: novaQtde } };
    });
  }, []);

  const agrupamentosFiltrados = agrupamentos.filter(a =>
    a.descricao.toLowerCase().includes(termoBusca.toLowerCase())
  );

  return {
    agrupamentos: agrupamentosFiltrados,
    agrupamentosAll: agrupamentos,
    sugestoesMap,
    carregando,
    termoBusca,
    setTermoBusca,
    carregarAgrupamentos,
    criarAgrupamento,
    excluirAgrupamento,
    renomearAgrupamento,
    decrementarSugestao,
  };
};
